// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseTable, Loading } from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { IScheduleDashboard } from '~/utils/interface/statistic';
import { IPaginationResponse } from '~/utils/interface/base';
import { getStatisticTodaySchedule } from '~/thunks/statistic/statisticThunk';
import { formatDurationTime, formatTitleCase, getFullName } from '~/utils/helper/common';
import { IHeaderCellType, IQueryParamBase } from '~/utils/interface/common';
import { DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE, EMPTY_VALUE } from '~/utils/constants/common';
// Styles, images, icons
import styles from './TodaysSchedule.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const columnsSchedule = (t: TFunction): IHeaderCellType<IScheduleDashboard>[] => {
  return [
    {
      id: 'name',
      label: t('schedule_today_title_jobs'),
      render: (record: IScheduleDashboard) => record?.title || EMPTY_VALUE,
    },
    {
      id: 'status',
      label: t('schedule_today_title_status'),
      render: (record: IScheduleDashboard) => formatTitleCase(record?.status) || EMPTY_VALUE,
    },
    {
      id: 'truck',
      label: t('schedule_today_title_truck_id'),
      render: (record: IScheduleDashboard) => record?.truck?.id || EMPTY_VALUE,
      minWidth: 90,
    },
    {
      id: 'foreman',
      label: t('schedule_today_title_foreman'),
      render: (record: IScheduleDashboard) => {
        return getFullName({
          firstName: record?.foreman?.firstName,
          lastName: record?.foreman?.lastName,
        });
      },
    },
    {
      id: 'duration',
      label: t('schedule_today_title_job_completion_time'),
      render: (record: IScheduleDashboard) => formatDurationTime(record?.estimate) || EMPTY_VALUE,
      minWidth: 170,
    },
  ];
};

const TodaysSchedule = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/dashboard']);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [paramObject, setParamObject] = useState<IQueryParamBase>({
    page: DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE,
  });

  const [listSchedule, setListSchedule] = useState<IScheduleDashboard[]>([]);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListTodaySchedule(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListTodaySchedule = (params: IQueryParamBase) => {
    setIsLoading(true);

    dispatch(getStatisticTodaySchedule(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination } = res.data;

        if (!responses) return;

        setListSchedule(responses);
        setPagination(pagination);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
  };
  //#endregion Handle Function

  return (
    <Box id='todaysScheduleComponent' className={cx('container')}>
      <Stack direction={'column'} spacing={'13px'}>
        <Typography className={cx('title')}>{t('dashboard_title_todays_schedule')}</Typography>

        <Typography className={cx('description')}>{t('dashboard_title_todays_schedule_description')}</Typography>
      </Stack>

      <Box className={cx('body')}>
        <BaseTable
          dataSource={listSchedule}
          currentPage={paramObject.page}
          totalPage={pagination?.totalPages}
          columns={columnsSchedule(t)}
          onPageChange={handlePageChange}
        />
      </Box>

      {isLoading && <Loading />}
    </Box>
  );
};

export default TodaysSchedule;
